import React, { useEffect, useState } from "react";
import "./Header.css";
import { apiFetch, getSyncSettings } from "../utils/api";
import UserMenu from "./UserMenu";

type SyncHealthState = "unknown" | "healthy" | "unhealthy";

type RuntimeVersionInfo = {
  version?: string;
  commitSha?: string;
};

const Header: React.FC = () => {
  const [syncHealth, setSyncHealth] = useState<SyncHealthState>("unknown");
  const [runtimeVersion, setRuntimeVersion] = useState<RuntimeVersionInfo | null>(null);

  const appVersion = import.meta.env.VITE_APP_VERSION;
  const appCommitSha = import.meta.env.VITE_APP_COMMIT_SHA;

  useEffect(() => {
    let mounted = true;

    const fetchHealth = async () => {
      try {
        const settings = await getSyncSettings();

        if (!mounted) return;
        setSyncHealth(settings.anyAdapterConnected ? 'healthy' : 'unhealthy');
      } catch {
        if (mounted) setSyncHealth('unhealthy');
      }
    };

    void fetchHealth();
    const intervalId = window.setInterval(() => {
      void fetchHealth();
    }, 30000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchRuntimeVersion = async () => {
      try {
        const response = await apiFetch('/api/version');

        if (!mounted || !response.ok) return;
        const data = (await response.json()) as RuntimeVersionInfo;
        setRuntimeVersion(data);
      } catch {
        if (mounted) setRuntimeVersion(null);
      }
    };

    void fetchRuntimeVersion();
    const intervalId = window.setInterval(() => {
      void fetchRuntimeVersion();
    }, 60000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const shortenSha = (sha?: string): string | undefined => {
    if (!sha || sha === 'unknown') return undefined;
    return sha.slice(0, 7);
  };

  const appVersionText = [appVersion, shortenSha(appCommitSha)]
    .filter(Boolean)
    .join(' · ');
  const runtimeVersionText = [runtimeVersion?.version, shortenSha(runtimeVersion?.commitSha)]
    .filter(Boolean)
    .join(' · ');
  const hasDiverged =
    Boolean(appVersion) && Boolean(runtimeVersion?.version) && appVersion !== runtimeVersion?.version;

  const statusTitle =
    syncHealth === 'healthy'
      ? 'Sync adapter connected'
      : syncHealth === 'unhealthy'
        ? 'No sync adapter connected'
        : 'Sync status unknown';

  return (
    <header className="app-header">
      <h1>
        <img src="/logo512.png" alt="Sieve" className="app-logo" />
        Sieve
      </h1>
      {/* Grouped so the title's centre column does not move when a version
          badge appears or the sync label is hidden on narrow screens. */}
      <div className="app-header-controls">
        <div className="sync-health" title={statusTitle} aria-label={statusTitle}>
          <span className={`sync-health-dot sync-health-dot--${syncHealth}`} />
          <span className="sync-health-label">Sync</span>
        </div>
        {appVersionText && (
          <div
            className={`version-hint ${hasDiverged ? 'version-hint--diverged' : ''}`}
            title={
              hasDiverged && runtimeVersionText
                ? `App ${appVersionText} / API ${runtimeVersionText}`
                : `App ${appVersionText}${runtimeVersionText ? ` / API ${runtimeVersionText}` : ''}`
            }
            aria-label="Version info"
          >
            {hasDiverged && <span className="version-hint-warning">!</span>}
            <span className="version-hint-label">{appVersionText}</span>
          </div>
        )}
        <UserMenu />
      </div>
    </header>
  );
};

export default Header;
